import type { ThunkAction, UnknownAction } from "@reduxjs/toolkit";
import t from "@/lang/lang";
import type { RootState } from "@/shared/lib/store/store";
import { Field, FieldLabel } from "@/shared/components/ui/field";
import { FieldErrors } from "@/shared/components/field/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
} from "@/shared/components/ui/input-group";
import { InputGroupInputDebounced } from "@/shared/components/inputs/input-group-input-debounced";
import { Spinner } from "@/shared/components/ui/spinner";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import { RtkQueryMutation } from "@/shared/lib/debounce/debounce";
import { inputValidationErrorMessages } from "@/shared/components/errors/input-error-messages";
import {
  parseRTKQueryError,
  rtkQueryBackendErrorIs,
} from "@/shared/components/errors/parse-r-t-k-query-error";
import { rtkErrIsValidationError } from "@/shared/lib/errors/validation-error";
import { useFieldErrors } from "@/shared/lib/errors/err-validation-failed-context";
import {
  ErrReferenceNumberConflict,
  Reference,
  ResetReferenceNumberRequest,
  ReferenceNumberPatchRequest,
} from "@/services/backend/documents/types/reference-number";

interface ReferencedEntity extends Reference {
  id: string;
}

export interface ReferenceNumberFieldProps<T extends ReferencedEntity> {
  /** The document as read from the RTK Query cache. */
  data: T;
  /**
   * Patches the show query cache of the document, see `updateCache` in
   * `services/backend/_lib/update-cache.ts`.
   */
  updateCache: (
    value: T,
  ) => ThunkAction<unknown, RootState, unknown, UnknownAction>;
  /**
   * The return value of the document's `use…PatchMutation()`. It is sent
   * `{ id, referenceNumber }` only.
   */
  patchMutation: RtkQueryMutation<ReferenceNumberPatchRequest, T>;
  /** The return value of the document's `use…ResetReferenceNumberMutation()`. */
  resetMutation: RtkQueryMutation<ResetReferenceNumberRequest, T>;
  /**
   * Disables the input and the reset. Required, so no caller forgets it: pass
   * at least `isImmutable(data)`.
   */
  disabled: boolean;
}

/**
 * ReferenceNumberField edits the reference number of a document. It saves
 * while the user types; the button inside the input restores the number
 * generated for the document.
 *
 * Mount it within the ErrValidationFailedContextProvider of its document. A
 * document nested in another one's page (e.g. the documentation of a work
 * order) needs a provider of its own, or its errors mix with the outer
 * document's.
 */
export function ReferenceNumberField<T extends ReferencedEntity>({
  data,
  updateCache,
  patchMutation,
  resetMutation,
  disabled,
}: ReferenceNumberFieldProps<T>) {
  const [set, { error: updateError }] = useOptimisticDebouncedMutation(
    { id: data.id, referenceNumber: data.referenceNumber },
    updateCache,
    patchMutation,
  );
  const [reset, { error: resetError, isLoading: isResetting }] = resetMutation;

  // Validation errors of the update are read from the context, where they
  // are reset on input. The other errors (e.g. a conflicting number) are
  // shown until the next save.
  const {
    errors,
    invalid,
    reset: resetFieldErrors,
  } = useFieldErrors("referenceNumber", [
    ...(rtkErrIsValidationError(updateError)
      ? []
      : referenceNumberErrors(updateError)),
    ...referenceNumberErrors(resetError),
  ]);

  return (
    // The gap and the label line height follow the inputs of
    // shared/components/form (Label + gap-1.5), so the field lines up with
    // the date inputs it shares a row with. The Field default (gap-3 and a
    // leading-snug label) would place the input ~5px lower than its
    // neighbour.
    // This may be removed when the other UI parts migrate to
    // the newer Shadcn convention of gap-3.
    <Field className="gap-1.5">
      <FieldLabel className="leading-none">{t("Nummer")}</FieldLabel>
      <InputGroup>
        <InputGroupInputDebounced
          value={data.referenceNumber}
          aria-invalid={invalid || undefined}
          disabled={disabled}
          onChange={(e) => {
            resetFieldErrors();
            set(
              { id: data.id, referenceNumber: e.target.value },
              { ...data, referenceNumber: e.target.value },
            );
          }}
        />
        <InputGroupAddon align="inline-end">
          <InputGroupButton
            aria-label={t("Generierte Nummer übernehmen")}
            title={t("Generierte Nummer übernehmen")}
            disabled={disabled || isResetting}
            onClick={() => {
              resetFieldErrors();
              reset({ id: data.id });
            }}
          >
            {isResetting ? <Spinner /> : t("Reset")}
          </InputGroupButton>
        </InputGroupAddon>
      </InputGroup>
      <FieldErrors errors={errors} />
    </Field>
  );
}

function referenceNumberErrors(error: unknown): string[] {
  if (!error) {
    return [];
  }

  if (rtkQueryBackendErrorIs(error, ErrReferenceNumberConflict)) {
    return [t("Nummer wird bereits von einem anderen Dokument verwendet.")];
  }

  if (rtkErrIsValidationError(error)) {
    return inputValidationErrorMessages(error, "referenceNumber");
  }

  const parsed = parseRTKQueryError(
    error as Parameters<typeof parseRTKQueryError>[0],
  );
  return parsed?.description ? [parsed.description] : [];
}
